import { useCallback, useMemo, useRef, useState } from 'react';
import type { BracketTreeMatch, BracketTreeRound, DrawPreviewMatch } from '@court-sync/shared';
import {
  buildBracketTree,
  getBracketKioskSegments,
  getSegmentRounds,
} from '@court-sync/shared';
import { playEventDisplayTheme } from '@/components/play/playEventDisplayTheme';
import { useBracketKioskCarousel } from '@/hooks/useBracketKioskCarousel';
import { cn } from '@/lib/utils';

interface PlayEventDisplayBracketTreeProps {
  matches: DrawPreviewMatch[];
  participantNamesById?: Record<string, string>;
  isPreview?: boolean;
  className?: string;
  /** Segmented kiosk carousel with vertical pan per segment (public/TV boards). */
  autoScrollLoop?: boolean;
  compactFooter?: boolean;
  celebrateSlotKeys?: Set<string>;
}

function BracketRoundColumn({
  round,
  compact,
  celebrateSlotKeys,
}: {
  round: BracketTreeRound;
  compact?: boolean;
  celebrateSlotKeys?: Set<string>;
}) {
  return (
    <section
      className={cn(
        'flex shrink-0 flex-col gap-3',
        compact ? 'w-52 lg:w-56' : 'w-56 lg:w-60'
      )}
    >
      <h4
        className={`shrink-0 text-center text-xs font-semibold uppercase tracking-wide ${playEventDisplayTheme.mutedFaint}`}
      >
        {round.label}
      </h4>
      <div className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')}>
        {round.matches.map((match) => (
          <BracketMatchCard
            key={`${round.roundNumber}-${match.matchIndex}`}
            match={match}
            compact={compact}
            celebrateSlotKeys={celebrateSlotKeys}
          />
        ))}
      </div>
    </section>
  );
}

function BracketMatchCard({
  match,
  compact,
  celebrateSlotKeys,
}: {
  match: BracketTreeMatch;
  compact?: boolean;
  celebrateSlotKeys?: Set<string>;
}) {
  const sidePadding = compact ? 'px-2 py-1.5' : 'px-2.5 py-2';

  const renderSideClass = (side: 'a' | 'b') => {
    const participantId = side === 'a' ? match.sideA.participantId : match.sideB.participantId;
    const isWinner = match.winnerSide === side && match.result != null && match.result !== 'unplayed';
    const celebrateKey = `${match.roundNumber}-${match.matchIndex}-${side}`;
    const isCelebrating = Boolean(celebrateSlotKeys?.has(celebrateKey));

    return cn(
      'rounded-md border leading-snug transition-shadow duration-500',
      sidePadding,
      participantId
        ? 'border-gray-200 bg-white font-medium text-gray-900 dark:border-white/10 dark:bg-black/30 dark:text-white'
        : 'border-dashed border-gray-300 italic text-gray-500 dark:border-white/15 dark:text-white/45',
      isWinner &&
        'border-emerald-500 bg-emerald-50 font-semibold text-emerald-950 shadow-sm dark:border-emerald-400/70 dark:bg-emerald-500/15 dark:text-emerald-100',
      isCelebrating &&
        'motion-safe:animate-pulse ring-2 ring-emerald-400/80 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
    );
  };

  return (
    <article className={cn(playEventDisplayTheme.panel, 'shadow-sm', compact ? 'p-2.5' : 'p-3')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p
          className={`text-[10px] font-semibold uppercase tracking-wide ${playEventDisplayTheme.mutedFaint}`}
        >
          M{match.matchIndex + 1}
        </p>
        {match.scoreLabel ? (
          <p className={`text-[10px] font-medium ${playEventDisplayTheme.mutedSubtle}`}>
            {match.scoreLabel}
          </p>
        ) : null}
      </div>
      <div className={cn('text-sm', compact ? 'space-y-1.5' : 'space-y-2')}>
        <div className={renderSideClass('a')}>{match.sideA.label}</div>
        <div className={renderSideClass('b')}>{match.sideB.label}</div>
      </div>
    </article>
  );
}

export function PlayEventDisplayBracketTree({
  matches,
  participantNamesById = {},
  isPreview = false,
  className,
  autoScrollLoop = false,
  compactFooter = false,
  celebrateSlotKeys,
}: PlayEventDisplayBracketTreeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const segmentRef = useRef<HTMLDivElement>(null);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);

  const tree = useMemo(
    () => buildBracketTree(matches, participantNamesById),
    [matches, participantNamesById]
  );

  const segments = useMemo(() => getBracketKioskSegments(tree), [tree]);

  const safeSegmentIndex =
    segments.length > 0 ? activeSegmentIndex % segments.length : 0;
  const activeSegment = segments[safeSegmentIndex] ?? null;
  const activeRounds = useMemo(
    () => (activeSegment ? getSegmentRounds(tree, activeSegment) : []),
    [activeSegment, tree]
  );

  const useCompactCards = Boolean(
    activeSegment?.roundNumbers.includes(1) &&
      (tree.rounds.find((round) => round.roundNumber === 1)?.matches.length ?? 0) > 8
  );

  const handleSegmentChange = useCallback((nextIndex: number) => {
    setActiveSegmentIndex(nextIndex);
  }, []);

  useBracketKioskCarousel({
    enabled: autoScrollLoop && segments.length > 0,
    segmentCount: segments.length,
    activeSegmentIndex: safeSegmentIndex,
    onSegmentChange: handleSegmentChange,
    containerRef,
    segmentRef,
  });

  if (tree.rounds.length === 0) {
    return null;
  }

  const bracketBody = (
    <div className={cn('flex gap-5', autoScrollLoop ? 'items-start' : 'min-w-max')}>
      {tree.rounds.map((round) => (
        <BracketRoundColumn
          key={round.roundNumber}
          round={round}
          celebrateSlotKeys={celebrateSlotKeys}
        />
      ))}
    </div>
  );

  if (autoScrollLoop && activeSegment) {
    return (
      <div
        ref={containerRef}
        className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', className)}
        aria-label="Tournament bracket"
      >
        <div className="mb-3 flex shrink-0 items-center justify-between gap-3 px-1">
          <p className={`text-sm font-semibold text-gray-900 dark:text-white`}>{activeSegment.label}</p>
          {segments.length > 1 ? (
            <p className={`text-xs ${playEventDisplayTheme.mutedSubtle}`}>
              {safeSegmentIndex + 1} / {segments.length}
            </p>
          ) : null}
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div ref={segmentRef} className="inline-block w-full will-change-transform">
            <div className="flex w-max gap-5">
              {activeRounds.map((round) => (
                <BracketRoundColumn
                  key={`${activeSegment.id}-${round.roundNumber}`}
                  round={round}
                  compact={useCompactCards}
                  celebrateSlotKeys={celebrateSlotKeys}
                />
              ))}
            </div>
          </div>
        </div>

        {isPreview && !compactFooter ? (
          <p className={`mt-3 shrink-0 text-xs ${playEventDisplayTheme.mutedSubtle}`}>
            Projected bracket from current entries. Final seeding may change before the draw starts.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto pb-1', className)}>
      {bracketBody}
      {isPreview && !compactFooter ? (
        <p className={`mt-3 text-xs ${playEventDisplayTheme.mutedSubtle}`}>
          Projected bracket from current entries. Final seeding may change before the draw starts.
        </p>
      ) : null}
    </div>
  );
}
